import React from 'react';

import {
  MCButton,
  MCError,
  SelectField,
  Steps,
  TextAreaField,
  TextField,
  View
} from 'src/components/basic';

import {
  checkSameAsAvatar,
  lang,
  objectToSelectList,
  remCss,
  scssVariables as variables,
  withRouter
} from 'src/helpers';
import LANG_CONST from 'src/helpers/lang/const';
import model from './model';
import { ThumbnailUploader } from 'src/components/complex';
import { Puff } from 'react-loader-spinner';
import SVGIcon, { BASE_ICONS } from 'src/components/svg';
import BaseComponent from 'src/components/BaseComponent';

class AccountClinicForm extends BaseComponent {
  state = { step: 0 };
  componentDidMount() {
    model.subscribe(this.subscription);
    model.resetState();
    model.resetApiState('signup');
    model.resetApiState('verifyFields');
    model.resetApiState('createAccount');
  }

  componentWillUnmount() {
    model.unsubscribe(this.subscription);
  }

  afterSubmit = (e) => {
    e.preventDefault();
    model.resetApiState('signup');
    model.resetApiState('verifyFields');
    model.resetApiState('createAccount');
    const step = this.state.step;
    const fields = {
      name: model.getState().name,
      username: model.getState().username
    };
    model.verifyFields('clinic', fields).then((ok) => {
      if (ok === true) {
        this.setState({ step: step + 1 });
      }
    });
  };

  renderBasic = () => {
    const createState = model.getApiState('signup');
    const { invalid } = createState;

    if (createState.error === 'EMAIL_TAKEN') {
      invalid['email'] = createState.errorMessage;
      createState.error = null;
    }
    if (createState.error === 'URLNAME_TAKEN') {
      invalid['username'] = createState.errorMessage;
      createState.error = null;
    }
    if (model.getApiState('verifyFields').error) {
      createState.errorMessage = model.getApiState('verifyFields').errorMessage;
      createState.error = model.getApiState('verifyFields').error;
    }

    return (
      <View style={{ gap: remCss(15), maxWidth: remCss(500) }}>
        {createState.error && <MCError error={createState.errorMessage} />}
        <ThumbnailUploader
          type={'round'}
          image={model.input('avatar')}
          setImage={(data) => {
            model.resetApiState('signup');
            model.handleChange('avatar', '');
            if (data) {
              checkSameAsAvatar(
                data,
                model.state.user?.connectedAccounts.filter((u) => !!u.avatar).map((u) => u.avatar)
              ).then((error) => {
                if (error) model.setInvalidApi('signup', { avatar: error });
                else model.handleChange('avatar', data);
              });
            }
          }}
          autoLoad={true}
          activeCloseBtn={false}
          style={{ width: 90, height: 90, marginLeft: 5 }}
        />
        {invalid?.avatar && <MCError error={invalid?.avatar} mcTextProps={{ primary: true }} />}
        <TextField
          autoFocus={true}
          template={6}
          name={'name'}
          value={model.input('name')}
          onChange={(n, v) => {
            model.resetApiState('signup');
            model.handleChangeForUsername(n, v);
          }}
          label={LANG_CONST.clinic_name}
          invalid={invalid}
        />
        <TextField
          autoFocus={true}
          template={6}
          name={'username'}
          type='text'
          value={model.input('username')}
          suffixEl={
            model.getApiState('verifyFields').loading ? (
              <div className={'mc-plr10 mc-mr5'}>
                <Puff
                  color={variables['colors-mc-primary']}
                  height={remCss(25)}
                  width={remCss(25)}
                />
              </div>
            ) : model.input('username') && !invalid['username'] ? (
              <div className={'mc-plr10 mc-mr5 mc-flex mc-flex-center'}>
                <SVGIcon
                  outline
                  circle
                  padding={remCss(5)}
                  iconname={BASE_ICONS.CHECK}
                  fill={variables['colors-mc-primary']}
                  height={remCss(25)}
                  width={remCss(25)}
                />
              </div>
            ) : (
              ''
            )
          }
          onChange={(n, v) => model.handleChangeForUsername(n, v)}
          label={LANG_CONST.username}
          invalid={invalid}
        />
        {model.state.usernameSuggestion && (
          <span className={'mc-label-416'}>
            {LANG_CONST.suggestion}:{' '}
            <span
              className={'mc-primary-416 mc-pointer'}
              onClick={() => {
                model.handleChange('username', model.state.usernameSuggestion);
              }}
            >
              {model.state.usernameSuggestion}
            </span>
          </span>
        )}
      </View>
    );
  };

  renderInfo = () => {
    const createState = model.getApiState('createAccount');
    const { invalid } = createState;
    return (
      <View style={{ gap: remCss(15) }}>
        <SelectField
          isMultipleSpecial={true}
          isClearable={false}
          template={6}
          width={'100%'}
          placeholder={LANG_CONST.text.enter_here}
          label={LANG_CONST.speciality}
          options={objectToSelectList(LANG_CONST.constants.specialist, true)}
          name={'specialist'}
          value={model.input('specialist')}
          onChange={(n, v) => {
            model.resetApiState('signup');
            model.handleChange(n, v);
          }}
          invalid={invalid}
        />
        <TextAreaField
          template={6}
          rows={4}
          name={'description'}
          label={'Description'}
          placeholder={''}
          value={model.input('description')}
          onChange={(n, v) => {
            model.resetApiState('signup');
            model.handleChange(n, v);
          }}
          invalid={invalid}
        />
      </View>
    );
  };

  render() {
    const step = this.state.step;

    const steps = [
      {
        name: 'base',
        title: LANG_CONST.titles.signup,
        description: LANG_CONST.text.signup_connect_clients,
        comp: this.renderBasic()
      },
      {
        name: 'info',
        title: 'Clinic Info',
        description: LANG_CONST.text.set_up_clinic_info,
        comp: this.renderInfo()
      }
    ];

    let loading =
      model.getApiState('signup').loading ||
      model.getApiState('verifyFields').loading ||
      model.getApiState('createAccount').loading;

    return (
      <div className={'mc-main-content mc-flex-col'}>
        <View
          style={
            this.props.screenDetail.isSmallScreen
              ? { padding: remCss(30), gap: remCss(30), flex: 1 }
              : { padding: remCss(30), flexDirection: 'row', gap: remCss(50), flex: 1 }
          }
        >
          <Steps list={steps} current={steps[step]?.name} screenDetail={this.props.screenDetail} />
          <div className={'mc-tab-box-opacity mc-grow'}>
            {steps.map(({ comp, title, description }, index) => (
              <div
                key={index}
                className={'mc-tab-content mc-flex-col ' + (index === step ? '' : 'mc-inactive')}
              >
                <div className={'mc-flex-10-col mc-mb20'}>
                  <div className='mc-value-936'>{lang.t(title)}</div>
                  <div className='mc-value-418'>{lang.t(description)}</div>
                </div>
                {comp}
              </div>
            ))}
          </div>
        </View>
        <div className={'mc-bt mc-flex mc-p30'}>
          {step > 0 && (
            <MCButton
              onClick={() => {
                this.setState({ step: step - 1 });
              }}
              medium
              primary
              style={{ width: remCss(120) }}
              radius
              light
              label={LANG_CONST.buttons.previous}
            />
          )}
          <div className={'mc-grow'}></div>
          <MCButton
            onClick={this.props.onCancel}
            medium
            disabled={loading}
            primary
            style={{ width: remCss(120) }}
            radius
            light
            label={LANG_CONST.buttons.cancel}
          />
          {step < steps.length - 1 ? (
            <MCButton
              medium
              radius
              dark
              loading={loading}
              disabled={loading}
              label={'Next'}
              style={{ width: remCss(120) }}
              onClick={this.afterSubmit}
            />
          ) : (
            <MCButton
              medium
              radius
              dark
              loading={loading}
              disabled={loading}
              label={'Create'}
              style={{ width: remCss(120) }}
              onClick={(e) => {
                e.preventDefault();
                model.resetApiState('signup');
                model.resetApiState('verifyFields');
                model.resetApiState('createAccount');
                model.createAccount();
              }}
            />
          )}
        </div>
      </div>
    );
  }
}

export default withRouter(AccountClinicForm);
